import { computed, inject, Injectable, signal } from '@angular/core';
import {
  catchError,
  finalize,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';

import { Notification } from '../services/notification';
import {
  ACTIVATE_SANDBOX_STEP,
  INTEGRATION_CHECKLIST_STEP_DEFINITIONS,
  INTEGRATION_TEST_SUITE_CASES,
  TEST_PAYMENT_LIFECYCLE_STAGES,
} from './integration-checklist.constants';
import {
  INTEGRATION_STEP_ACTIONS,
  IntegrationChecklistData,
  IntegrationChecklistStep,
  IntegrationChecklistStepAction,
  IntegrationTestCaseResult,
  RunTestSuiteResult,
  TestPaymentStageResult,
  TestSuiteSummary,
  WebhookConfigResult,
} from './integration-checklist.model';
import { IntegrationChecklistService } from './integration-checklist.service';

@Injectable({ providedIn: 'root' })
export class IntegrationChecklistStore {
  private readonly service = inject(IntegrationChecklistService);
  private readonly notify = inject(Notification);

  private readonly _data = signal<IntegrationChecklistData | null>(null);
  private readonly _initialLoading = signal(false);
  private readonly _completingStep = signal<number | null>(null);
  private readonly _testPaymentStages = signal<TestPaymentStageResult[] | null>(
    null,
  );
  private readonly _testSuiteResults = signal<
    IntegrationTestCaseResult[] | null
  >(null);
  private readonly _testSuiteSummary = signal<TestSuiteSummary | null>(null);
  private readonly _webhookConfig = signal<WebhookConfigResult | null>(null);
  private readonly _sandboxActivated = signal(false);

  readonly data = this._data.asReadonly();
  readonly initialLoading = this._initialLoading.asReadonly();
  readonly completingStep = this._completingStep.asReadonly();
  readonly testPaymentStages = this._testPaymentStages.asReadonly();
  readonly testSuiteResults = this._testSuiteResults.asReadonly();
  readonly testSuiteSummary = this._testSuiteSummary.asReadonly();
  readonly webhookConfig = this._webhookConfig.asReadonly();

  readonly steps = computed(() => this.mergeSteps(this._data()?.steps ?? []));
  readonly isLive = computed(() => this._data()?.isLive ?? false);
  readonly isGoLiveEligible = computed(
    () => this._data()?.isGoLiveEligible ?? false,
  );
  readonly completedSteps = computed(() => this._data()?.completedSteps ?? 0);
  readonly sandboxActivated = computed(
    () => this._sandboxActivated() || !!this._data()?.steps?.length,
  );
  readonly totalSteps = computed(() => this.steps().length + 1);
  readonly totalCompletedSteps = computed(
    () => this.completedSteps() + (this.sandboxActivated() ? 1 : 0),
  );
  readonly showSandboxDashboard = computed(() => !this.isLive());
  readonly testSuiteAllPassed = computed(() => {
    const summary = this._testSuiteSummary();
    if (summary) {
      return summary.allPassed;
    }

    const results = this._testSuiteResults();
    return !!results?.length && results.every((test: any) => test.passed);
  });

  loadChecklist(): Observable<boolean> {
    if (!this._data()) {
      this._initialLoading.set(true);
    }

    return this.service.getChecklist().pipe(
      tap((response: any) => {
        if (response?.success && response.data) {
          this._sandboxActivated.set(true);
          this.applyChecklistData(response.data);
        }
      }),
      map((response: any) => !!(response?.success && response.data)),
      catchError(() => {
        this.notify.error('Error', 'Failed to load integration checklist.');
        return of(false);
      }),
      finalize(() => this._initialLoading.set(false)),
    );
  }

  completeStep(
    step: IntegrationChecklistStep,
    payload: Record<string, unknown> = {},
  ): Observable<boolean> {
    return this.executeStepAction(
      step,
      INTEGRATION_STEP_ACTIONS[step.step],
      payload,
    );
  }

  activateSandbox(): Observable<boolean> {
    this._completingStep.set(ACTIVATE_SANDBOX_STEP);

    return this.service.activateSandbox().pipe(
      switchMap((response: any) => {
        if (!response?.success) {
          this.notify.error(
            'Error',
            response?.message || 'Failed to activate sandbox.',
          );
          return of(false);
        }

        this._sandboxActivated.set(true);
        this.notify.success(
          'Sandbox activated',
          response.message || 'Your sandbox environment is ready.',
        );

        return this.loadChecklist().pipe(
          map((loaded) => {
            if (!loaded) {
              this.notify.error(
                'Error',
                'Sandbox activated but failed to refresh checklist.',
              );
            }
            return loaded;
          }),
        );
      }),
      catchError(() => {
        this.notify.error('Error', 'Failed to activate sandbox.');
        return of(false);
      }),
      finalize(() => this._completingStep.set(null)),
    );
  }

  configureWebhook(
    step: IntegrationChecklistStep,
    webhookUrl: string,
  ): Observable<boolean> {
    this._completingStep.set(step.step);

    return this.service.configureWebhook({ webhookUrl }).pipe(
      switchMap((response: any) => {
        if (response?.success && response.data) {
          this._webhookConfig.set(response.data);

          return this.loadChecklist().pipe(
            map((loaded) => {
              if (!loaded) {
                this.notify.error(
                  'Error',
                  'Webhook saved but failed to refresh checklist.',
                );
                return false;
              }

              if (response.data.pingSuccessful) {
                this.notify.success(
                  'Webhook configured',
                  response.message ||
                    'Webhook URL configured. Test ping received HTTP 200.',
                );
              } else {
                this.notify.error(
                  'Webhook ping failed',
                  response.message ||
                    'Webhook URL was saved, but the test ping did not succeed.',
                );
              }

              return true;
            }),
          );
        }

        this.notify.error(
          'Error',
          response?.message || 'Failed to configure webhook URL.',
        );
        return of(false);
      }),
      catchError(() => {
        this.notify.error('Error', 'Failed to configure webhook URL.');
        return of(false);
      }),
      finalize(() => this._completingStep.set(null)),
    );
  }

  runTestPayment(step: IntegrationChecklistStep): Observable<boolean> {
    this._testPaymentStages.set(
      TEST_PAYMENT_LIFECYCLE_STAGES.map((stage) => ({
        id: stage.id,
        label: stage.label,
        completed: false,
      })),
    );

    return this.executeStepAction(step, 'test-payment', {}, (data) => {
      this._testPaymentStages.set(this.normalizeTestPaymentStages(data));
    });
  }

  runTestSuite(step: IntegrationChecklistStep): Observable<boolean> {
    this._completingStep.set(step.step);

    return this.service.runTestSuite().pipe(
      switchMap((response: any) => {
        if (response?.success && response.data) {
          this.applyRunTestSuiteResult(response.data);

          return this.loadChecklist().pipe(
            map((loaded) => {
              if (!loaded) {
                this.notify.error(
                  'Error',
                  'Test suite finished but failed to refresh checklist.',
                );
                return false;
              }

              if (response.data.allPassed) {
                this.notify.success(
                  'All tests passed',
                  response.message ||
                    `${response.data.passedCount} of ${response.data.results.length} integration tests passed.`,
                );
              } else {
                this.notify.error(
                  'Some tests failed',
                  response.message ||
                    `${response.data.failedCount} test(s) failed. Review the cases below and re-run the suite.`,
                );
              }

              return response.data.allPassed;
            }),
          );
        }

        this.notify.error(
          'Error',
          response?.message || 'Failed to run integration test suite.',
        );
        return of(false);
      }),
      catchError(() => {
        this._testSuiteResults.set(
          this.buildFailedTestSuiteResults('Request failed. Please try again.'),
        );
        this._testSuiteSummary.set({
          allPassed: false,
          passedCount: 0,
          failedCount: INTEGRATION_TEST_SUITE_CASES.length,
        });
        this.notify.error('Error', 'Failed to run integration test suite.');
        return of(false);
      }),
      finalize(() => this._completingStep.set(null)),
    );
  }

  submitEndToEndTest(
    step: IntegrationChecklistStep,
    file: File,
    notes: string,
  ): Observable<boolean> {
    if (step.completed) {
      return of(true);
    }

    this._completingStep.set(step.step);

    return this.service.submitTestSuite(file, notes).pipe(
      switchMap((response: any) => {
        if (!response?.success) {
          this.notify.error(
            'Error',
            response?.message || 'Failed to submit end-to-end test results.',
          );
          return of(false);
        }

        return this.loadChecklist().pipe(
          map((loaded) => {
            if (!loaded) {
              this.notify.error(
                'Error',
                'End-to-end test submitted but failed to refresh checklist.',
              );
              return false;
            }

            this.notify.success(
              'End-to-end test submitted',
              response.message ||
                'Your test suite results were submitted successfully.',
            );
            return true;
          }),
        );
      }),
      catchError(() => {
        this.notify.error('Error', 'Failed to submit end-to-end test results.');
        return of(false);
      }),
      finalize(() => this._completingStep.set(null)),
    );
  }

  private executeStepAction(
    step: IntegrationChecklistStep,
    action: IntegrationChecklistStepAction | undefined,
    payload: Record<string, unknown> = {},
    onSuccess?: (data: IntegrationChecklistData) => void,
  ): Observable<boolean> {
    if (!action) {
      return of(false);
    }

    if (step.completed && action !== 'run-test-suite') {
      return of(true);
    }

    this._completingStep.set(step.step);

    return this.service.completeStep(action, payload).pipe(
      switchMap((response: any) => {
        if (!response?.success) {
          this.notify.error(
            'Error',
            response?.message || `Failed to complete ${step.name}.`,
          );
          return of(false);
        }

        if (this.isIntegrationChecklistData(response.data)) {
          onSuccess?.(response.data);
        }

        return this.loadChecklist().pipe(
          map((loaded) => {
            if (!loaded) {
              this.notify.error(
                'Error',
                `${step.name} completed but failed to refresh checklist.`,
              );
              return false;
            }

            if (
              action === 'test-payment' &&
              !this._testPaymentStages()?.some((stage: any) => stage.completed)
            ) {
              this._testPaymentStages.set(
                TEST_PAYMENT_LIFECYCLE_STAGES.map((stage) => ({
                  id: stage.id,
                  label: stage.label,
                  completed: true,
                })),
              );
            }

            if (action === 'test-payment') {
              this.notify.success(
                'Test payment complete',
                response.message ||
                  'Sandbox test payment simulated successfully using pre-seeded accounts.',
              );
            } else {
              this.notify.success(
                'Step completed',
                response.message || `${step.name} marked as complete.`,
              );
            }

            return true;
          }),
        );
      }),
      catchError(() => {
        if (action === 'test-payment') {
          this._testPaymentStages.set(
            TEST_PAYMENT_LIFECYCLE_STAGES.map((stage) => ({
              id: stage.id,
              label: stage.label,
              completed: false,
            })),
          );
        }
        this.notify.error('Error', `Failed to complete ${step.name}.`);
        return of(false);
      }),
      finalize(() => this._completingStep.set(null)),
    );
  }

  private applyChecklistData(data: IntegrationChecklistData): void {
    this._data.set({
      ...data,
      steps: this.mergeSteps(data.steps ?? []),
    });

    if (data.testPaymentStages?.length) {
      this._testPaymentStages.set(data.testPaymentStages);
    }

    if (data.testSuiteResults?.length) {
      this._testSuiteResults.set(this.normalizeTestSuiteResults(data));
    }
  }

  private mergeSteps(
    apiSteps: IntegrationChecklistStep[],
  ): IntegrationChecklistStep[] {
    return INTEGRATION_CHECKLIST_STEP_DEFINITIONS.map((definition) => {
      const match =
        apiSteps.find((step) => step.step === definition.step) ??
        apiSteps.find(
          (step) => step.name.toLowerCase() === definition.name.toLowerCase(),
        );

      return (
        match ?? {
          step: definition.step,
          name: definition.name,
          completed: false,
          completedAt: null,
          detail: null,
        }
      );
    });
  }

  private normalizeTestPaymentStages(
    data: IntegrationChecklistData,
  ): TestPaymentStageResult[] {
    if (data.testPaymentStages?.length) {
      return data.testPaymentStages;
    }

    return TEST_PAYMENT_LIFECYCLE_STAGES.map((stage) => ({
      id: stage.id,
      label: stage.label,
      completed: true,
    }));
  }

  private normalizeTestSuiteResults(
    data: IntegrationChecklistData,
  ): IntegrationTestCaseResult[] {
    const apiResults = data.testSuiteResults ?? [];
    const assumeAllPassed = apiResults.length === 0;

    return INTEGRATION_TEST_SUITE_CASES.map((definition) => {
      const match = apiResults.find(
        (result) =>
          result.testKey === definition.testKey ||
          result.name.toLowerCase() === definition.name.toLowerCase() ||
          result.name.toLowerCase() === definition.scenarioName.toLowerCase(),
      );

      if (match) {
        return {
          testKey: definition.testKey,
          name: match.name || definition.name,
          description: match.description ?? null,
          passed: match.passed,
          detail: match.detail ?? null,
          remediation: match.remediation ?? definition.remediation,
          runAt: match.runAt ?? null,
        };
      }

      return {
        testKey: definition.testKey,
        name: definition.name,
        description: null,
        passed: assumeAllPassed,
        detail: assumeAllPassed
          ? null
          : 'No result returned for this test case.',
        remediation: definition.remediation,
        runAt: null,
      };
    });
  }

  private applyRunTestSuiteResult(data: RunTestSuiteResult): void {
    this._testSuiteResults.set(this.mapRunTestSuiteResults(data));
    this._testSuiteSummary.set({
      allPassed: data.allPassed,
      passedCount: data.passedCount,
      failedCount: data.failedCount,
    });
  }

  private mapRunTestSuiteResults(
    data: RunTestSuiteResult,
  ): IntegrationTestCaseResult[] {
    return INTEGRATION_TEST_SUITE_CASES.map((definition) => {
      const match = data.results.find(
        (result) =>
          result.scenarioName.toLowerCase() ===
            definition.scenarioName.toLowerCase() ||
          result.scenarioName.toLowerCase() === definition.name.toLowerCase(),
      );

      if (match) {
        return {
          testKey: definition.testKey,
          name: match.scenarioName,
          description: match.description,
          passed: match.passed,
          detail: match.detail,
          remediation: match.remediationGuidance ?? definition.remediation,
          runAt: match.runAt,
        };
      }

      return {
        testKey: definition.testKey,
        name: definition.name,
        description: null,
        passed: false,
        detail: 'No result returned for this test case.',
        remediation: definition.remediation,
        runAt: null,
      };
    });
  }

  private buildFailedTestSuiteResults(
    message: string,
  ): IntegrationTestCaseResult[] {
    return INTEGRATION_TEST_SUITE_CASES.map((definition) => ({
      testKey: definition.testKey,
      name: definition.name,
      description: null,
      passed: false,
      detail: message,
      remediation: definition.remediation,
      runAt: null,
    }));
  }

  private isIntegrationChecklistData(
    data: unknown,
  ): data is IntegrationChecklistData {
    return (
      !!data &&
      typeof data === 'object' &&
      'steps' in data &&
      Array.isArray((data as IntegrationChecklistData).steps)
    );
  }
}
